import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import ProfessionalDashboardNavbar from 'pages/professional-dashboard/components/ProfessionalDashboardNavbar';
import Footer from 'pages/landing-page/components/Footer';
import { Checkbox } from 'components/ui/Checkbox';
import { X, CreditCard, Loader2 } from 'lucide-react';
import { cn } from 'utils/cn';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

// ─── Stripe promise (singleton) ───────────────────────────────────────────────
const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

// ─── Inner form inside Elements ───────────────────────────────────────────────
const UpdateCardForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setFormError('');
    try {
      const { error, setupIntent } = await stripe?.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location?.origin + '/professional/settings',
        },
        redirect: 'if_required',
      });
      if (error) {
        setFormError(error?.message || 'Errore durante la configurazione della carta.');
        setProcessing(false);
        return;
      }
      if (setupIntent?.status === 'succeeded') {
        // Synchronously pull card data from Stripe (fallback in case webhook is late)
        try {
          await supabase?.functions?.invoke('sync-card-from-stripe', { body: {} });
        } catch (syncErr) {
          console.warn('sync-card-from-stripe failed:', syncErr);
        }
        onSuccess?.();
      } else {
        setFormError('Stato imprevisto: ' + setupIntent?.status);
        setProcessing(false);
      }
    } catch (err) {
      setFormError(err?.message || 'Errore imprevisto.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement />
      {formError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{formError}</p>
      )}
      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
          style={{ backgroundColor: '#104d98' }}
        >
          {processing ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Elaborazione...</>
          ) : (
            'Salva carta'
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="px-5 py-2.5 rounded-lg text-sm font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-60"
        >
          Annulla
        </button>
      </div>
      <p className="text-xs text-gray-400 text-center">🔒 I tuoi dati sono protetti e crittografati da Stripe</p>
    </form>
  );
};

// ─── Update Payment Modal ─────────────────────────────────────────────────────
const UpdatePaymentModal = ({ clientSecret, onSuccess, onClose }) => {
  if (!clientSecret) return null;

  const options = {
    clientSecret,
    appearance: { theme: 'stripe' },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Chiudi"
        >
          <X size={20} />
        </button>
        <h3 className="text-lg font-semibold text-gray-900 mb-5">Aggiorna metodo di pagamento</h3>
        <Elements stripe={stripePromise} options={options}>
          <UpdateCardForm onSuccess={onSuccess} onCancel={onClose} />
        </Elements>
      </div>
    </div>
  );
};

// ─── Pro Avatar Icons ──────────────────────────────────────────────────────────
const PRO_AVATAR_ICONS = [
  { id: 'person', label: 'Persona', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
    </svg>
  )},
  { id: 'wrench', label: 'Chiave inglese', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>
    </svg>
  )},
  { id: 'hammer', label: 'Martello', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M15 12l-8.5 8.5c-.83.83-2.17.83-3 0 0 0 0 0 0 0a2.12 2.12 0 010-3L12 9"/>
      <path d="M17.64 15L22 10.64"/><path d="M20.91 11.7l-1.25-1.25c-.6-.6-.93-1.4-.93-2.25v-.86L16.01 4.6a5.56 5.56 0 00-3.94-1.64H9l.92.82A6.18 6.18 0 0112 8.4v1.56l2 2h2.47l2.26 1.91"/>
    </svg>
  )},
  { id: 'zap', label: 'Fulmine', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  )},
  { id: 'droplets', label: 'Idraulica', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z"/>
      <path d="M12.56 6.6A10.97 10.97 0 0014 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 01-11.91 4.97"/>
    </svg>
  )},
  { id: 'leaf', label: 'Verde', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M17 8C8 10 5.9 16.17 3.82 19.34a1 1 0 001.38 1.4C7.2 19.1 9.5 17 12 17c4 0 8-3 8-8 0-3-1-5-3-7z"/>
      <path d="M3 21c0-4 2-7 5-9"/>
    </svg>
  )},
  { id: 'star', label: 'Stella', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  )},
  { id: 'home', label: 'Casa', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"/><path d="M9 21V12h6v9"/>
    </svg>
  )},
];

// ─── Font Picker Infrastructure (Pro) ─────────────────────────────────────────
const FONT_LINK_ID_PRO = 'volaptic-pro-fonts';
const FONT_STYLE_ID_PRO = 'volaptic-pro-font-style';
const STORAGE_KEY_FONT_PRO = 'volaptic_pro_font';

const FONTS_PRO = [
  { id: 'default', label: 'Default', family: null, description: 'Font di sistema' },
  { id: 'plus-jakarta-sans', label: 'Plus Jakarta Sans', family: "'Plus Jakarta Sans', sans-serif", googleName: 'Plus+Jakarta+Sans:wght@400;500;600;700' },
  { id: 'dm-sans', label: 'DM Sans', family: "'DM Sans', sans-serif", googleName: 'DM+Sans:wght@400;500;600;700' },
  { id: 'outfit', label: 'Outfit', family: "'Outfit', sans-serif", googleName: 'Outfit:wght@400;500;600;700' },
];

const injectGoogleFontsPro = () => {
  if (document.getElementById(FONT_LINK_ID_PRO)) return;
  const fontNames = FONTS_PRO?.filter(f => f?.googleName)?.map(f => f?.googleName)?.join('&family=');
  const link = document.createElement('link');
  link.id = FONT_LINK_ID_PRO;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${fontNames}&display=swap`;
  document.head?.appendChild(link);
};

const applyProFont = (fontId) => {
  let styleEl = document.getElementById(FONT_STYLE_ID_PRO);
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = FONT_STYLE_ID_PRO;
    document.head?.appendChild(styleEl);
  }
  const font = FONTS_PRO?.find(f => f?.id === fontId);
  if (font?.family) {
    styleEl.textContent = `body { font-family: ${font?.family} !important; }`;
  } else {
    styleEl.textContent = `body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important; }`;
  }
};

// ─── Inline WorkAreaSection (reused logic from registration-step-2) ───────────
const WorkAreaSettingsSection = ({ initialAreas, initialExtended, onSave, saving }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState(initialAreas || []);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [extendedAvailability, setExtendedAvailability] = useState(initialExtended?.enabled || false);
  const [allItaly, setAllItaly] = useState(initialExtended?.allItaly || false);
  const [includeSardiniaSicily, setIncludeSardiniaSicily] = useState(initialExtended?.includeSardiniaSicily || false);
  const [sessionToken, setSessionToken] = useState(() => generateSessionToken());
  const searchTimeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  const MAPBOX_TOKEN = (() => {
    const envToken = import.meta.env?.VITE_MAPBOX_TOKEN;
    if (envToken && envToken !== 'your-mapbox-token-here' && !envToken?.includes('placeholder')) {
      return envToken;
    }
    return 'pk.eyJ1IjoicGVkZXJ6b2xsaXNpbW9uZSIsImEiOiJjbWt5OTA3OHowNG02M2VzZTFoYWJwN3E2In0.HCcTrRyl9L0dWbcIp0x1iQ';
  })();

  function generateSessionToken() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'?.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v?.toString(16);
    });
  }

  useEffect(() => {
    setSelectedAreas(initialAreas || []);
  }, [initialAreas?.length]);

  useEffect(() => {
    if (initialExtended) {
      setExtendedAvailability(initialExtended?.enabled || false);
      setAllItaly(initialExtended?.allItaly || false);
      setIncludeSardiniaSicily(initialExtended?.includeSardiniaSicily || false);
    }
  }, [initialExtended?.enabled, initialExtended?.allItaly, initialExtended?.includeSardiniaSicily]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef?.current && !wrapperRef?.current?.contains(event?.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchLocations = async (query) => {
    if (!query || query?.length < 2) { setSuggestions([]); return; }
    setIsLoading(true);
    try {
      const url = `https://api.mapbox.com/search/searchbox/v1/suggest?q=${encodeURIComponent(query)}&language=it&country=IT&types=region,district,place,locality&limit=5&session_token=${sessionToken}&access_token=${MAPBOX_TOKEN}`;
      const response = await fetch(url);
      if (!response?.ok) throw new Error(`Mapbox API returned ${response.status}`);
      const data = await response?.json();
      setSuggestions(data?.suggestions || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching location suggestions:', error?.message || error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e?.target?.value;
    setSearchQuery(value);
    if (searchTimeoutRef?.current) clearTimeout(searchTimeoutRef?.current);
    searchTimeoutRef.current = setTimeout(() => searchLocations(value), 300);
  };

  const retrieveLocation = async (mapboxId) => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/search/searchbox/v1/retrieve/${mapboxId}?session_token=${sessionToken}&access_token=${MAPBOX_TOKEN}`
      );
      if (!response?.ok) throw new Error('Failed to retrieve location');
      const data = await response?.json();
      return data?.features?.[0];
    } catch (error) {
      console.error('Error retrieving location:', error);
      return null;
    }
  };

  const handleSelectLocation = async (suggestion) => {
    if (selectedAreas?.length >= 3) return;
    const alreadySelected = selectedAreas?.some((area) => area?.mapbox_id === suggestion?.mapbox_id);
    if (alreadySelected) { setSearchQuery(''); setSuggestions([]); setShowSuggestions(false); return; }
    const locationDetails = await retrieveLocation(suggestion?.mapbox_id);
    const newArea = {
      id: suggestion?.mapbox_id,
      name: suggestion?.name,
      fullName: suggestion?.full_address || suggestion?.name,
      mapbox_id: suggestion?.mapbox_id,
      details: locationDetails,
    };
    const updatedAreas = [...selectedAreas, newArea];
    setSelectedAreas(updatedAreas);
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSessionToken(generateSessionToken());
  };

  const handleRemoveArea = (areaId) => {
    setSelectedAreas((prev) => prev?.filter((area) => area?.id !== areaId));
  };

  const handleSave = () => {
    onSave?.({
      areas: selectedAreas,
      extended: { enabled: extendedAvailability, allItaly, includeSardiniaSicily },
    });
  };

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Zone primarie di lavoro</label>
        <p className="text-xs text-gray-500 mb-2">Seleziona le zone dove operi di norma (max 3)</p>
        <div className="relative" ref={wrapperRef}>
          <input
            type="text"
            placeholder="Cerca città, provincia o regione..."
            value={searchQuery}
            onChange={handleSearchChange}
            disabled={selectedAreas?.length >= 3}
            className={cn(
              'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              selectedAreas?.length >= 3 && 'bg-gray-100 cursor-not-allowed'
            )}
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
            </div>
          )}
          {showSuggestions && suggestions?.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
              {suggestions?.map((suggestion) => {
                const isSelected = selectedAreas?.some((area) => area?.mapbox_id === suggestion?.mapbox_id);
                return (
                  <button
                    key={suggestion?.mapbox_id}
                    type="button"
                    onClick={() => handleSelectLocation(suggestion)}
                    disabled={isSelected || selectedAreas?.length >= 3}
                    className={cn(
                      'w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors',
                      isSelected && 'bg-gray-50 text-gray-400 cursor-not-allowed',
                      selectedAreas?.length >= 3 && !isSelected && 'cursor-not-allowed opacity-50'
                    )}
                  >
                    <div className="font-medium text-sm text-gray-900">{suggestion?.name}</div>
                    {suggestion?.place_formatted && (
                      <div className="text-xs text-gray-500">{suggestion?.place_formatted}</div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected chips */}
        {selectedAreas?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {selectedAreas?.map((area) => (
              <div
                key={area?.id}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium"
              >
                <span>{area?.name}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveArea(area?.id)}
                  className="ml-1 hover:bg-blue-100 rounded-full p-0.5 transition-colors"
                  aria-label={`Rimuovi ${area?.name}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-2 text-sm text-gray-600">{selectedAreas?.length}/3 zone selezionate</div>
      </div>

      {/* Extended Availability */}
      <div className="pt-4 border-t border-gray-200">
        <Checkbox
          id="settings-extended-availability"
          checked={extendedAvailability}
          onChange={(e) => {
            const checked = e?.target?.checked;
            setExtendedAvailability(checked);
            if (!checked) { setAllItaly(false); setIncludeSardiniaSicily(false); }
          }}
          label="Valuto anche richieste fuori zona"
        />
        {extendedAvailability && (
          <div className="mt-3 ml-6 space-y-2">
            <Checkbox
              id="settings-all-italy"
              checked={allItaly}
              onChange={(e) => setAllItaly(e?.target?.checked)}
              label="In tutta Italia"
            />
            <Checkbox
              id="settings-include-sardinia-sicily"
              checked={includeSardiniaSicily}
              onChange={(e) => setIncludeSardiniaSicily(e?.target?.checked)}
              label="Include Sardegna e Sicilia"
            />
            <p className="text-xs text-gray-500 mt-2">
              Queste opzioni indicano solo disponibilità a valutare richieste, non garantiscono accettazione.
            </p>
          </div>
        )}
      </div>

      {/* Save CTA */}
      <div className="pt-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
          style={{ backgroundColor: '#104d98' }}
        >
          {saving ? 'Salvataggio...' : 'Salva zone'}
        </button>
      </div>
    </div>
  );
};

// ─── Main Settings Page ────────────────────────────────────────────────────────
const ProfessionalSettings = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [profileData, setProfileData] = useState({ profession: '', email: '' });
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [workAreas, setWorkAreas] = useState([]);
  const [extendedData, setExtendedData] = useState({ enabled: false, allItaly: false, includeSardiniaSicily: false });
  const [workAreasLoaded, setWorkAreasLoaded] = useState(false);
  const [workAreasSaving, setWorkAreasSaving] = useState(false);
  const [workAreasSuccess, setWorkAreasSuccess] = useState('');
  const [workAreasError, setWorkAreasError] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [docData, setDocData] = useState({ document_type: '', file_name: '', storage_path: '' });
  const [docLoading, setDocLoading] = useState(false);
  const [accountStatus, setAccountStatus] = useState('');
  const [pageLoading, setPageLoading] = useState(true);
  const [allProfessions, setAllProfessions] = useState([]);
  const [selectedProfessionIds, setSelectedProfessionIds] = useState([]);
  const [professionSearchQuery, setProfessionSearchQuery] = useState('');
  const [showProfessionDropdown, setShowProfessionDropdown] = useState(false);
  const [professionsSaving, setProfessionsSaving] = useState(false);
  const [professionsSuccess, setProfessionsSuccess] = useState('');
  const [professionsError, setProfessionsError] = useState('');
  const [legacyProfessionText, setLegacyProfessionText] = useState('');

  // Avatar state
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [avatarSuccess, setAvatarSuccess] = useState('');
  const avatarInputRef = useRef(null);

  // Avatar mode / icon state
  const [avatarMode, setAvatarMode] = useState('photo');
  const [avatarIcon, setAvatarIcon] = useState(null);
  const [iconSaving, setIconSaving] = useState(false);
  const [iconSuccess, setIconSuccess] = useState('');

  // Font picker state (Pro)
  const [selectedFontPro, setSelectedFontPro] = useState('default');
  const [fontSavingPro, setFontSavingPro] = useState(false);
  const [fontSuccessPro, setFontSuccessPro] = useState('');

  const [nameSaving, setNameSaving] = useState(false);
  const [nameSuccess, setNameSuccess] = useState('');

  // Payment method state
  const [cardData, setCardData] = useState({ card_last4: null, card_brand: null, card_exp_month: null, card_exp_year: null, stripe_customer_id: null });
  const [cardLoading, setCardLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentClientSecret, setPaymentClientSecret] = useState(null);
  const [paymentToast, setPaymentToast] = useState('');
  const [paymentError, setPaymentError] = useState('');

  // ── Silent on-load call to update-payment-method ──────────────────────────
  useEffect(() => {
    if (authLoading || !user) return;

    const silentFetchPaymentMethod = async () => {
      try {
        const { data, error } = await supabase?.functions?.invoke('update-payment-method');
        if (error || !data) return;

        // Populate card UI from currentCard returned by the edge function
        if (data?.currentCard) {
          setCardData({
            card_last4: data?.currentCard?.last4 || null,
            card_brand: data?.currentCard?.brand || null,
            card_exp_month: data?.currentCard?.exp_month || null,
            card_exp_year: data?.currentCard?.exp_year || null,
            stripe_customer_id: null,
          });
        }

        // Store clientSecret so buttons can open the modal immediately
        if (data?.clientSecret) {
          setPaymentClientSecret(data?.clientSecret);
        }
      } catch (_err) {
        // Silent — no UI feedback
      }
    };

    silentFetchPaymentMethod();
  }, [user, authLoading]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login-form'); return; }

    const loadData = async () => {
      setPageLoading(true);
      try {
        const { data: userData } = await supabase
          ?.from('users')
          ?.select('profession, email, account_status, role, full_name, avatar_url, avatar_icon, card_last4, card_brand, card_exp_month, card_exp_year, stripe_customer_id')
          ?.eq('id', user?.id)
          ?.single();

        if (userData) {
          setProfileData({ profession: userData?.profession || '', email: userData?.email || '', fullName: userData?.full_name || '' });
          setAccountStatus(userData?.account_status || '');
          if (userData?.avatar_url) setAvatarUrl(userData?.avatar_url);
          if (userData?.avatar_icon) {
            setAvatarIcon(userData?.avatar_icon);
            if (!userData?.avatar_url) setAvatarMode('icon');
          }
          setCardData({
            card_last4: userData?.card_last4 || null,
            card_brand: userData?.card_brand || null,
            card_exp_month: userData?.card_exp_month || null,
            card_exp_year: userData?.card_exp_year || null,
            stripe_customer_id: userData?.stripe_customer_id || null,
          });
        }

        const { data: areasData } = await supabase
          ?.from('professional_work_areas')
          ?.select('*')
          ?.eq('user_id', user?.id);

        if (areasData) {
          const mapped = areasData?.map((a) => ({
            id: a?.mapbox_id || a?.id,
            name: a?.name,
            fullName: a?.full_name || a?.name,
            mapbox_id: a?.mapbox_id || a?.id,
            db_id: a?.id,
          }));
          setWorkAreas(mapped);
        }

        const { data: extData } = await supabase
          ?.from('professional_extended_availability')
          ?.select('*')
          ?.eq('user_id', user?.id)
          ?.single();

        if (extData) {
          setExtendedData({
            enabled: extData?.all_italy === true || extData?.include_sardinia_sicily === true,
            allItaly: extData?.all_italy || false,
            includeSardiniaSicily: extData?.include_sardinia_sicily || false,
          });
        }

        setWorkAreasLoaded(true);

        const { data: vatData } = await supabase
          ?.from('professional_vat_info')
          ?.select('vat_number')
          ?.eq('user_id', user?.id)
          ?.single();

        if (vatData) setVatNumber(vatData?.vat_number || '');

        const { data: docRow } = await supabase
          ?.from('professional_documents')
          ?.select('document_type, file_name, storage_path')
          ?.eq('user_id', user?.id)
          ?.limit(1)
          ?.single();

        if (docRow) setDocData({ document_type: docRow?.document_type || '', file_name: docRow?.file_name || '', storage_path: docRow?.storage_path || '' });

        // Fetch all available professions
        const { data: professionsListData } = await supabase?.from('professions')?.select('id, name, category')?.order('category', { ascending: true });
        if (professionsListData) setAllProfessions(professionsListData);

        // Fetch this professional's currently selected professions
        const { data: myProfessionsData } = await supabase?.from('professional_professions')?.select('profession_id')?.eq('user_id', user?.id);
        if (myProfessionsData) setSelectedProfessionIds(myProfessionsData?.map(r => r?.profession_id));

        // Fallback: if junction table is empty but users.profession has a value, try to match it
        if ((!myProfessionsData || myProfessionsData?.length === 0) && userData?.profession) {
          if (professionsListData) {
            const match = professionsListData?.find(p => p?.name?.toLowerCase() === (userData?.profession || '')?.toLowerCase());
            if (match) {
              setSelectedProfessionIds([match?.id]);
              setLegacyProfessionText('');
            } else {
              setLegacyProfessionText(userData?.profession);
            }
          } else {
            setLegacyProfessionText(userData?.profession);
          }
        }

      } catch (err) {
        console.error('Settings load error:', err);
      } finally {
        setPageLoading(false);
      }
    };

    loadData();
  }, [user, authLoading, navigate]);

  // ── Refresh card data from DB ──────────────────────────────────────────────
  const refreshCardData = async () => {
    try {
      const { data: { user: freshUser } } = await supabase?.auth?.getUser();
      if (!freshUser) return;
      const { data } = await supabase
        ?.from('users')
        ?.select('card_last4, card_brand, card_exp_month, card_exp_year, stripe_customer_id')
        ?.eq('id', freshUser?.id)
        ?.single();
      if (data) {
        setCardData({
          card_last4: data?.card_last4 || null,
          card_brand: data?.card_brand || null,
          card_exp_month: data?.card_exp_month || null,
          card_exp_year: data?.card_exp_year || null,
          stripe_customer_id: data?.stripe_customer_id || null,
        });
      }
    } catch (err) {
      console.error('Card refresh error:', err);
    }
  };

  // ── Open payment modal: call edge function to get SetupIntent ─────────────
  const handleOpenPaymentModal = async () => {
    // If we already have a clientSecret from the silent on-load call, use it directly
    if (paymentClientSecret) {
      setShowPaymentModal(true);
      return;
    }

    // Fallback: fetch a fresh SetupIntent if clientSecret is not yet available
    setCardLoading(true);
    setPaymentError('');
    try {
      const { data, error } = await supabase?.functions?.invoke('update-payment-method');
      if (error) throw new Error(error?.message || 'Errore durante la preparazione del pagamento.');
      if (!data?.clientSecret) throw new Error('Nessun clientSecret ricevuto.');
      setPaymentClientSecret(data?.clientSecret);
      setShowPaymentModal(true);
    } catch (err) {
      console.error('Payment modal error:', err);
      setPaymentError(err?.message || 'Errore imprevisto. Riprova.');
    } finally {
      setCardLoading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    setPaymentClientSecret(null);
    setPaymentToast('Carta aggiornata');
    await refreshCardData();
    setTimeout(() => setPaymentToast(''), 4000);
  };

  const handlePaymentModalClose = () => {
    setShowPaymentModal(false);
    setPaymentClientSecret(null);
  };

  const handlePasswordSubmit = async (e) => {
    e?.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordForm?.newPass !== passwordForm?.confirm) {
      setPasswordError('Le password non coincidono');
      return;
    }
    if (passwordForm?.newPass?.length < 8) {
      setPasswordError('La nuova password deve essere di almeno 8 caratteri');
      return;
    }

    setPasswordLoading(true);

    const { error: signInError } = await supabase?.auth?.signInWithPassword({
      email: profileData?.email,
      password: passwordForm?.current,
    });

    if (signInError) {
      setPasswordError('Password attuale non corretta');
      setPasswordLoading(false);
      return;
    }

    const { error: updateError } = await supabase?.auth?.updateUser({ password: passwordForm?.newPass });

    if (updateError) {
      setPasswordError(updateError?.message || 'Errore durante l\'aggiornamento della password');
    } else {
      setPasswordSuccess('Password aggiornata');
      setPasswordForm({ current: '', newPass: '', confirm: '' });
    }

    setPasswordLoading(false);
  };

  const handleSaveWorkAreas = async ({ areas, extended }) => {
    setWorkAreasSaving(true);
    setWorkAreasSuccess('');
    setWorkAreasError('');

    try {
      const { data: { user: freshUser }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !freshUser) throw new Error('Impossibile verificare l\'utente. Riprova.');

      const userId = freshUser?.id;

      const { error: deleteError } = await supabase?.from('professional_work_areas')?.delete()?.eq('user_id', userId);

      if (deleteError) throw deleteError;

      if (areas?.length > 0) {
        const toInsert = areas?.map((area, idx) => ({
          user_id: userId,
          name: area?.name,
          full_name: area?.fullName || area?.name || null,
          mapbox_id: area?.mapbox_id ?? '',
          is_primary: idx === 0,
        }));

        const { error: insertError } = await supabase?.from('professional_work_areas')?.insert(toInsert);

        if (insertError) throw insertError;
      }

      const { error: upsertError } = await supabase
        ?.from('professional_extended_availability')
        ?.upsert({
          user_id: userId,
          all_italy: extended?.allItaly || false,
          include_sardinia_sicily: extended?.includeSardiniaSicily || false,
        }, { onConflict: 'user_id' });

      if (upsertError) throw upsertError;

      setWorkAreasSuccess('Zone aggiornate con successo');
    } catch (err) {
      console.error('Work areas save error:', err);
      setWorkAreasError(err?.message || 'Errore durante il salvataggio delle zone di lavoro');
    } finally {
      setWorkAreasSaving(false);
    }
  };

  const handleSaveProfessions = async () => {
    setProfessionsSaving(true);
    setProfessionsSuccess('');
    try {
      const { data: { user: freshUser }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !freshUser) throw new Error('Impossibile verificare l\'utente.');
      const userId = freshUser?.id;

      await supabase?.from('professional_professions')?.delete()?.eq('user_id', userId);

      if (selectedProfessionIds?.length > 0) {
        const toInsert = selectedProfessionIds?.map(profession_id => ({ user_id: userId, profession_id }));
        const { error: insertError } = await supabase?.from('professional_professions')?.insert(toInsert);
        if (insertError) throw insertError;
      }

      setProfessionsSuccess('Salvato');
      setTimeout(() => setProfessionsSuccess(''), 3000);
    } catch (err) {
      console.error('Professions save error:', err);
    } finally {
      setProfessionsSaving(false);
    }
  };

  const handleSaveIcon = async () => {
    if (!avatarIcon) return;
    setIconSaving(true);
    setIconSuccess('');
    try {
      const { data: { user: freshUser } } = await supabase?.auth?.getUser();
      const { error } = await supabase
        ?.from('users')
        ?.update({ avatar_icon: avatarIcon, avatar_url: null })
        ?.eq('id', freshUser?.id);
      if (error) throw error;
      setIconSuccess('Icona salvata!');
      setAvatarUrl(null);
      window.dispatchEvent(new CustomEvent('volaptic-avatar-update', {
        detail: { icon: avatarIcon, url: null }
      }));
      setTimeout(() => setIconSuccess(''), 3000);
    } catch (err) {
      console.error('Icon save error:', err);
    } finally {
      setIconSaving(false);
    }
  };

  const handleViewDocument = async () => {
    if (!docData?.storage_path) return;
    setDocLoading(true);
    try {
      const { data, error } = await supabase?.storage
        ?.from('identity-documents')
        ?.createSignedUrl(docData?.storage_path, 60);

      if (error) throw error;
      if (data?.signedUrl) window.open(data?.signedUrl, '_blank');
    } catch (err) {
      console.error('Signed URL error:', err);
    } finally {
      setDocLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    setAvatarError('');
    setAvatarSuccess('');

    const allowedTypes = ['image/jpeg', 'image/png'];
    if (!allowedTypes?.includes(file?.type)) {
      setAvatarError('Formato non supportato. Usa JPEG o PNG.');
      return;
    }
    if (file?.size > 2 * 1024 * 1024) {
      setAvatarError('Il file supera i 2MB. Scegli un\'immagine più piccola.');
      return;
    }

    setAvatarUploading(true);
    try {
      const path = `avatars/${user?.id}.jpg`;
      const { error: upErr } = await supabase?.storage
        ?.from('professional-portfolio')
        ?.upload(path, file, { upsert: true, contentType: file?.type });
      if (upErr) throw upErr;

      const { data: urlData } = supabase?.storage
        ?.from('professional-portfolio')
        ?.getPublicUrl(path);
      const publicUrl = urlData?.publicUrl + '?v=' + Date.now();

      await supabase?.from('users')?.update({ avatar_url: publicUrl, avatar_icon: null })?.eq('id', user?.id);
      setAvatarUrl(publicUrl);
      setAvatarIcon(null);
      window.dispatchEvent(new CustomEvent('volaptic-avatar-update', {
        detail: { icon: null, url: publicUrl }
      }));
      setAvatarSuccess('Foto profilo aggiornata con successo!');
      setTimeout(() => setAvatarSuccess(''), 4000);
    } catch (err) {
      console.error('Avatar upload error:', err);
      setAvatarError(err?.message || 'Errore durante il caricamento. Riprova.');
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef?.current) avatarInputRef.current.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    setAvatarError('');
    setAvatarSuccess('');
    try {
      await supabase?.from('users')?.update({ avatar_url: null })?.eq('id', user?.id);
      setAvatarUrl(null);
      window.dispatchEvent(new CustomEvent('volaptic-avatar-update', {
        detail: { icon: avatarIcon, url: null }
      }));
      setAvatarSuccess('Foto profilo rimossa.');
      setTimeout(() => setAvatarSuccess(''), 3000);
    } catch (err) {
      console.error('Avatar remove error:', err);
      setAvatarError('Errore durante la rimozione. Riprova.');
    }
  };

  // Font picker effects & handler (Pro)
  useEffect(() => { injectGoogleFontsPro(); }, []);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY_FONT_PRO);
    if (saved) {
      setSelectedFontPro(saved);
      applyProFont(saved);
    }
  }, []);

  const handleSaveFontPro = () => {
    setFontSavingPro(true);
    localStorage.setItem(STORAGE_KEY_FONT_PRO, selectedFontPro);
    applyProFont(selectedFontPro);
    setFontSuccessPro('Font salvato!');
    setTimeout(() => { setFontSuccessPro(''); setFontSavingPro(false); }, 1500);
  };

  const handleSaveName = async () => {
    setNameSaving(true);
    setNameSuccess('');
    try {
      const { data: { user: freshUser }, error: authError } =
        await supabase?.auth?.getUser();
      if (authError || !freshUser) throw new Error('Errore verifica utente.');
      const { error } = await supabase
        ?.from('users')
        ?.update({ full_name: profileData?.fullName?.trim() || null })
        ?.eq('id', freshUser?.id);
      if (error) throw error;
      setNameSuccess('Nome salvato');
      window.dispatchEvent(new CustomEvent('volaptic-name-update', {
        detail: { name: profileData?.fullName?.trim() || '' }
      }));
      setTimeout(() => setNameSuccess(''), 3000);
    } catch (err) {
      console.error('Name save error:', err);
    } finally {
      setNameSaving(false);
    }
  };

  const renderStatusBadge = () => {
    const badges = {
      active: { label: '✓ Account verificato', className: 'bg-green-100 text-green-800 border border-green-200' },
      pending: { label: '⏳ Verifica in corso', className: 'bg-yellow-100 text-yellow-800 border border-yellow-200' },
      rejected: { label: '✗ Verifica non completata', className: 'bg-red-100 text-red-800 border border-red-200' },
      suspended: { label: '⚠️ Account sospeso', className: 'bg-orange-100 text-orange-800 border border-orange-200' },
    };
    const badge = badges?.[accountStatus] || badges?.pending;
    return (
      <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${badge?.className}`}>
        {badge?.label}
      </span>
    );
  };

  if (authLoading || pageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Caricamento impostazioni...</p>
        </div>
      </div>
    );
  }

  // Compute initials for avatar placeholder
  const displayName = profileData?.fullName || profileData?.email?.split('@')?.[0] || '';
  const avatarInitials = displayName
    ? displayName?.trim()?.split(' ')?.map(w => w?.[0])?.slice(0, 2)?.join('')?.toUpperCase()
    : '??';

  // Card brand display helper
  const formatCardBrand = (brand) => {
    if (!brand) return '';
    return brand?.charAt(0)?.toUpperCase() + brand?.slice(1)?.toLowerCase();
  };

  const formatExpMonth = (month) => {
    if (!month) return '';
    return String(month)?.padStart(2, '0');
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <ProfessionalDashboardNavbar />
      <main className="max-w-[720px] mx-auto px-4 py-10">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Impostazioni Account</h1>

        {/* ── SECTION 1: PROFILO ─────────────────────────────────────────────── */}
        <section className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Il tuo profilo</h2>
            <div className="space-y-4">

              {/* Nome visualizzato row */}
              <div className="py-3 border-b border-gray-100">
                <label className="text-sm font-medium text-gray-500 block mb-2">
                  Nome visualizzato
                </label>
                <input
                  type="text"
                  value={profileData?.fullName || ''}
                  onChange={(e) => setProfileData(prev => ({
                    ...prev,
                    fullName: e?.target?.value
                  }))}
                  placeholder="Come vuoi apparire nell'intestazione"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#32b3e5] focus:border-transparent transition-all"
                  maxLength={60}
                />
                <div className="mt-3 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleSaveName}
                    disabled={nameSaving}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                    style={{ backgroundColor: '#2780b5' }}
                  >
                    {nameSaving ? 'Salvataggio...' : 'Salva nome'}
                  </button>
                  {nameSuccess && (
                    <span className="text-sm text-green-600 font-medium">
                      ✓ {nameSuccess}
                    </span>
                  )}
                </div>
              </div>

              {/* Email row */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-500">Email</span>
                <span className="text-sm text-gray-900 font-medium">{profileData?.email || '—'}</span>
              </div>

              {/* Profession multi-select row */}
              <div className="py-3">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-500">Servizi offerti</span>
                  <span className="text-xs text-gray-400">{selectedProfessionIds?.length}/5 selezionati</span>
                </div>

                {/* Selected chips */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {allProfessions
                    ?.filter(p => selectedProfessionIds?.includes(p?.id))
                    ?.map(p => (
                      <div
                        key={p?.id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium"
                        style={{ backgroundColor: '#e8f0fb', color: '#104d98' }}
                      >
                        <span>{p?.name}</span>
                        <button
                          type="button"
                          onClick={() => setSelectedProfessionIds(prev => prev?.filter(id => id !== p?.id))}
                          className="ml-1 rounded-full p-0.5 hover:opacity-70 transition-opacity"
                          aria-label={`Rimuovi ${p?.name}`}
                        >
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                            <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                        </button>
                      </div>
                    ))
                  }
                  {legacyProfessionText && (
                    <div
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium"
                      style={{ backgroundColor: '#fef3c7', color: '#92400e' }}
                      title="Professione dalla registrazione — seleziona quella corretta dalla lista e salva per aggiornare"
                    >
                      <span>{legacyProfessionText}</span>
                      <span className="text-xs ml-1 opacity-60">(legacy)</span>
                    </div>
                  )}
                </div>

                {/* Add button + dropdown */}
                {selectedProfessionIds?.length < 5 && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => { setShowProfessionDropdown(true); setProfessionSearchQuery(''); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-gray-300 text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                    >
                      <span>+ Aggiungi servizio</span>
                    </button>
                    {showProfessionDropdown && (
                      <div className="absolute z-20 top-9 left-0 w-72 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                        <div className="p-2 border-b border-gray-100">
                          <input
                            type="text"
                            autoFocus
                            placeholder="Cerca servizio..."
                            value={professionSearchQuery}
                            onChange={e => setProfessionSearchQuery(e?.target?.value)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="max-h-52 overflow-y-auto">
                          {allProfessions
                            ?.filter(p =>
                              !selectedProfessionIds?.includes(p?.id) &&
                              (professionSearchQuery === '' ||
                               p?.name?.toLowerCase()?.includes(professionSearchQuery?.toLowerCase()) ||
                               p?.category?.toLowerCase()?.includes(professionSearchQuery?.toLowerCase()))
                            )
                            ?.map(p => (
                              <button
                                key={p?.id}
                                type="button"
                                onClick={() => {
                                  setSelectedProfessionIds(prev => [...prev, p?.id]);
                                  setShowProfessionDropdown(false);
                                  setProfessionSearchQuery('');
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                              >
                                <div className="text-sm text-gray-900">{p?.name}</div>
                                <div className="text-xs text-gray-400">{p?.category}</div>
                              </button>
                            ))
                          }
                          {allProfessions?.filter(p =>
                            !selectedProfessionIds?.includes(p?.id) &&
                            (professionSearchQuery === '' ||
                             p?.name?.toLowerCase()?.includes(professionSearchQuery?.toLowerCase()) ||
                             p?.category?.toLowerCase()?.includes(professionSearchQuery?.toLowerCase()))
                          )?.length === 0 && (
                            <div className="px-4 py-3 text-sm text-gray-400 text-center">Nessun risultato</div>
                          )}
                        </div>
                        <div className="p-2 border-t border-gray-100">
                          <button
                            type="button"
                            onClick={() => { setShowProfessionDropdown(false); setProfessionSearchQuery(''); }}
                            className="w-full text-center text-xs text-gray-400 hover:text-gray-600 py-1"
                          >
                            Chiudi
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Save button */}
                <div className="mt-4 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleSaveProfessions}
                    disabled={professionsSaving}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                    style={{ backgroundColor: '#104d98' }}
                  >
                    {professionsSaving ? 'Salvataggio...' : 'Salva profilo'}
                  </button>
                  {professionsSuccess && (
                    <span className="text-sm text-green-600 font-medium">✓ {professionsSuccess}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION 1b: IL TUO AVATAR ─────────────────────────────────────── */}
        <section className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Il tuo avatar</h2>
            <p className="text-sm text-gray-500 mb-5">Scegli un'icona o carica una foto profilo.</p>

            {/* Toggle */}
            <div className="flex gap-2 mb-5 p-1 bg-gray-100 rounded-lg w-fit">
              <button
                type="button"
                onClick={() => setAvatarMode('icon')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                  avatarMode === 'icon' ?'bg-white text-[#013776] shadow-sm' :'text-gray-600 hover:text-gray-900'
                }`}
              >
                Icona
              </button>
              <button
                type="button"
                onClick={() => setAvatarMode('photo')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                  avatarMode === 'photo' ?'bg-white text-[#013776] shadow-sm' :'text-gray-600 hover:text-gray-900'
                }`}
              >
                Foto
              </button>
            </div>

            {/* Icon grid — shown when avatarMode === 'icon' */}
            {avatarMode === 'icon' && (
              <div>
                <div className="grid grid-cols-4 gap-3 mb-4">
                  {PRO_AVATAR_ICONS?.map((icon) => {
                    const isSelected = avatarIcon === icon?.id;
                    return (
                      <button
                        key={icon?.id}
                        type="button"
                        onClick={() => setAvatarIcon(icon?.id)}
                        title={icon?.label}
                        className={`flex flex-col items-center justify-center gap-1.5 rounded-xl p-3 border-2 transition-all ${
                          isSelected
                            ? 'border-[#32b3e5] bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {icon?.svg(isSelected ? '#32b3e5' : '#6b7280')}
                        <span className="text-xs text-gray-500">{icon?.label}</span>
                      </button>
                    );
                  })}
                </div>
                {iconSuccess && (
                  <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3">
                    {iconSuccess}
                  </p>
                )}
                <button
                  type="button"
                  onClick={handleSaveIcon}
                  disabled={iconSaving || !avatarIcon}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                  style={{ backgroundColor: '#2780b5' }}
                >
                  {iconSaving ? 'Salvataggio...' : 'Salva icona'}
                </button>
              </div>
            )}

            {/* Photo block — shown when avatarMode === 'photo' */}
            {avatarMode === 'photo' && (
              <div>
                <div className="flex items-center gap-5">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt="Foto profilo"
                      style={{
                        width: '96px',
                        height: '96px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        flexShrink: 0,
                        boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                        border: '2px solid #e2e8f0',
                      }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '96px',
                        height: '96px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #32b3e5 0%, #013776 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                      }}
                    >
                      <span style={{ color: '#fff', fontWeight: 700, fontSize: '28px', letterSpacing: '1px' }}>
                        {avatarInitials}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/jpeg,image/png"
                      style={{ display: 'none' }}
                      onChange={handleAvatarUpload}
                    />
                    <button
                      type="button"
                      onClick={() => avatarInputRef?.current?.click()}
                      disabled={avatarUploading}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                      style={{ backgroundColor: '#104d98' }}
                    >
                      {avatarUploading ? 'Caricamento...' : 'Carica foto'}
                    </button>
                    {avatarUrl && (
                      <button
                        type="button"
                        onClick={handleRemoveAvatar}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors"
                      >
                        Rimuovi foto
                      </button>
                    )}
                    <p className="text-xs text-gray-400">JPEG o PNG, max 2MB</p>
                  </div>
                </div>
                {avatarError && (
                  <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{avatarError}</p>
                )}
                {avatarSuccess && (
                  <p className="mt-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">✓ {avatarSuccess}</p>
                )}
              </div>
            )}
          </div>
        </section>

        {/* ── SECTION 1c: CARATTERE ─────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Carattere</h2>
          <p className="text-sm text-gray-500 mb-5">
            Scegli il font che preferisci per la tua interfaccia.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {FONTS_PRO?.map(font => {
              const isSelected = selectedFontPro === font?.id;
              return (
                <button
                  key={font?.id}
                  type="button"
                  onClick={() => setSelectedFontPro(font?.id)}
                  style={font?.family ? { fontFamily: font?.family } : {}}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    isSelected
                      ? 'border-[#32b3e5] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-sm font-semibold text-gray-900">{font?.label}</div>
                  {font?.description && (
                    <div className="text-xs text-gray-500 mt-1">{font?.description}</div>
                  )}
                </button>
              );
            })}
          </div>
          {fontSuccessPro && (
            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3">
              {fontSuccessPro}
            </p>
          )}
          <button
            type="button"
            onClick={handleSaveFontPro}
            disabled={fontSavingPro}
            className="px-5 py-2.5 bg-[#2780b5] text-white rounded-lg hover:bg-[#013776] transition-colors text-sm font-medium disabled:opacity-50"
          >
            {fontSavingPro ? 'Salvataggio...' : 'Salva font'}
          </button>
        </div>

        {/* ── SECTION 2: SICUREZZA ───────────────────────────────────────────── */}
        <section className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Modifica password</h2>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password attuale</label>
                <input
                  type="password"
                  required
                  value={passwordForm?.current}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, current: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nuova password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={passwordForm?.newPass}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPass: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Conferma nuova password</label>
                <input
                  type="password"
                  required
                  value={passwordForm?.confirm}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirm: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {passwordError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{passwordError}</p>
              )}
              {passwordSuccess && (
                <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{passwordSuccess}</p>
              )}

              <button
                type="submit"
                disabled={passwordLoading}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                style={{ backgroundColor: '#104d98' }}
              >
                {passwordLoading ? 'Aggiornamento...' : 'Aggiorna password'}
              </button>
            </form>
          </div>
        </section>

        {/* ── SECTION 3: ZONE DI LAVORO ──────────────────────────────────────── */}
        <section className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Zone di lavoro</h2>
            {workAreasSuccess && (
              <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-4">{workAreasSuccess}</p>
            )}
            {workAreasError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">{workAreasError}</p>
            )}
            {workAreasLoaded && (
              <WorkAreaSettingsSection
                initialAreas={workAreas}
                initialExtended={extendedData}
                onSave={handleSaveWorkAreas}
                saving={workAreasSaving}
              />
            )}
          </div>
        </section>

        {/* ── SECTION 4: PARTITA IVA ─────────────────────────────────────────── */}
        <section className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Partita IVA</h2>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm font-medium text-gray-500">Partita IVA</span>
              <span className="text-sm text-gray-900 font-medium font-mono">{vatNumber || '—'}</span>
            </div>
          </div>
        </section>

        {/* ── SECTION 5: DOCUMENTO DI IDENTITÀ ──────────────────────────────── */}
        <section className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Documento di identità</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-500">Tipo documento</span>
                <span className="text-sm text-gray-900 font-medium capitalize">{docData?.document_type || '—'}</span>
              </div>
              {docData?.file_name && (
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={handleViewDocument}
                    disabled={docLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-60"
                    style={{ backgroundColor: '#104d98' }}
                  >
                    {docLoading ? 'Caricamento...' : 'Visualizza documento'}
                  </button>
                </div>
              )}
              {!docData?.file_name && (
                <p className="text-sm text-gray-500">Nessun documento caricato.</p>
              )}
            </div>
          </div>
        </section>

        {/* ── SECTION 6: STATO ACCOUNT ───────────────────────────────────────── */}
        <section className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Stato verifica account</h2>
            {renderStatusBadge()}
          </div>
        </section>

        {/* ── SECTION 7: METODO DI PAGAMENTO ────────────────────────────────── */}
        <section className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Metodo di pagamento</h2>

            {paymentToast && (
              <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-4">
                ✓ {paymentToast}
              </p>
            )}

            {paymentError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
                {paymentError}
              </p>
            )}

            {cardData?.card_last4 ? (
              /* ── Card on file ── */
              (<div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center shadow-sm">
                    <CreditCard size={20} className="text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCardBrand(cardData?.card_brand)} •••• •••• •••• {cardData?.card_last4}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Scadenza: {formatExpMonth(cardData?.card_exp_month)}/{cardData?.card_exp_year}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleOpenPaymentModal}
                  disabled={cardLoading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                  style={{ backgroundColor: '#104d98' }}
                >
                  {cardLoading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Caricamento...</>
                  ) : (
                    'Sostituisci carta'
                  )}
                </button>
              </div>)
            ) : (
              /* ── No card ── */
              (<div className="space-y-4">
                <p className="text-sm text-gray-500">Nessun metodo di pagamento configurato</p>
                <button
                  type="button"
                  onClick={handleOpenPaymentModal}
                  disabled={cardLoading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                  style={{ backgroundColor: '#104d98' }}
                >
                  {cardLoading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Caricamento...</>
                  ) : (
                    'Aggiungi carta'
                  )}
                </button>
              </div>)
            )}

            <p className="mt-5 text-xs text-gray-400 leading-relaxed">
              La tua carta verrà addebitata il 1° di ogni mese per la fatturazione dei preventivi accettati. Soglia minima €5,00; sotto soglia accumuliamo al mese successivo.
            </p>
          </div>
        </section>

        <Link
          to="/professional-dashboard"
          className="flex items-center justify-center gap-2 w-full max-w-[720px] mx-auto mt-8 py-3 rounded-xl border border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 shadow-sm transition-colors"
        >
          ← Torna alla dashboard
        </Link>
      </main>
      <Footer />
      {/* ── Payment Modal ── */}
      {showPaymentModal && paymentClientSecret && (
        <UpdatePaymentModal
          clientSecret={paymentClientSecret}
          onSuccess={handlePaymentSuccess}
          onClose={handlePaymentModalClose}
        />
      )}
    </div>
  );
};

export default ProfessionalSettings;